/**
 * The immutable puzzle definition. This is the contract between hand-authored (and,
 * later, generated) puzzle JSON, the api that serves it, and the game engine.
 *
 * Coordinates are zero-based `{ row, col }` with row 0 at the top.
 */

export const PUZZLE_SCHEMA_VERSION = 1;

export interface Coord {
	row: number;
	col: number;
}

/** Which way an answer's letters run once it has started. */
export type Axis = 'across' | 'down';

/**
 * The arrow printed in a clue cell, describing where its answer starts and which way
 * it runs:
 *
 * - `right`      starts in the cell to the right of the clue, runs across  (→)
 * - `down`       starts in the cell below the clue, runs down              (↓)
 * - `right-down` starts in the cell to the right, then runs down           (↱ bent)
 * - `down-right` starts in the cell below, then runs across               (↳ bent)
 *
 * Bent arrows let edge answers be clued from a neighbouring clue cell.
 */
export type ClueDirection = 'right' | 'down' | 'right-down' | 'down-right';

export const CLUE_DIRECTIONS: readonly ClueDirection[] = ['right', 'down', 'right-down', 'down-right'];

/** What the clue shows. Text for now; icon and image are reserved for picture clues. */
export type ClueContent =
	| { kind: 'text'; text: string }
	/** `icon` names an entry in our own SVG icon set; `label` is the accessible text. */
	| { kind: 'icon'; icon: string; label: string }
	| { kind: 'image'; src: string; alt: string };

export interface Clue {
	id: string;
	/** The clue cell that prints this clue. One cell may hold up to two clues. */
	cell: Coord;
	direction: ClueDirection;
	content: ClueContent;
	answerId: string;
}

export interface Answer {
	id: string;
	/** Uppercase A–Z only, no spaces. */
	text: string;
	start: Coord;
	axis: Axis;
	/** Optional display form for the result/share screens, e.g. "ICE CREAM". */
	display?: string;
}

/** Reserved for hidden themes (not built yet). Opaque to the engine. */
export interface PuzzleTheme {
	answer: string;
	/** Answer ids that belong to the theme. */
	members: string[];
	reveal?: string;
}

/**
 * A special puzzle's type: a seasonal or one-off puzzle the app dresses for (Halloween:
 * Nib in his witch's hat). It belongs to the puzzle, not the date, so it shows whenever
 * the puzzle is played, archive included. Ordinary puzzles have none.
 */
export const PUZZLE_TYPES = ['halloween', 'christmas'] as const;
export type PuzzleType = (typeof PUZZLE_TYPES)[number];

/** 1 = Monday-easy … 5 = Sunday-hard. */
export type Difficulty = 1 | 2 | 3 | 4 | 5;

/**
 * A daily's level, easiest first. Each date has one daily per level, sharing the date's
 * public number ("Arrow No. 42 · Hard"). The canonical list: the api, engine and web
 * app all use this one.
 */
export const LEVELS = ['easy', 'medium', 'hard'] as const;
export type Level = (typeof LEVELS)[number];
/** What a client gets when it doesn't name a level (days from before levels are medium). */
export const DEFAULT_LEVEL: Level = 'medium';

export function isLevel(value: unknown): value is Level {
	return value === 'easy' || value === 'medium' || value === 'hard';
}

/** A difficulty's level when the puzzle doesn't name one: 1–2 easy, 3 medium, 4–5 hard. */
export function levelForDifficulty(difficulty: number): Level {
	return difficulty <= 2 ? 'easy' : difficulty <= 3 ? 'medium' : 'hard';
}

export interface Puzzle {
	schemaVersion: typeof PUZZLE_SCHEMA_VERSION;
	id: string;
	title: string;
	difficulty: Difficulty;
	/**
	 * Optional: the level this puzzle is scheduled at. Without it the level follows
	 * the difficulty (`levelForDifficulty`).
	 */
	level?: Level;
	width: number;
	height: number;
	/**
	 * One string per row, each exactly `width` characters:
	 *   `A`–`Z`  a letter cell (the character is the solution letter)
	 *   `#`      a clue cell
	 *   `.`      a blank cell (no clue, no letter; e.g. the top-left corner)
	 */
	grid: string[];
	answers: Answer[];
	clues: Clue[];
	theme?: PuzzleTheme;
	/** A special puzzle's type (see {@link PUZZLE_TYPES}). */
	type?: PuzzleType;
	author?: string;
	notes?: string;
}

export type CellKind = 'letter' | 'clue' | 'blank';

/**
 * A puzzle's shape, safe to show before playing: what a blank printed forme shows and
 * nothing more. The api sends it with archive and practice listings so covers can be
 * drawn without fetching the puzzle. It never holds a letter or a clue's words.
 */
export interface PuzzleShape {
	/**
	 * One string per row, `width` characters each:
	 *   `_`  a letter cell (empty: no solution letter)
	 *   `#`  a clue cell
	 *   `.`  a blank cell
	 */
	grid: string[];
	/** Each clue's arrow, in the puzzle's clue order: `[row, col, direction]` of its clue cell. */
	arrows: Array<[number, number, ClueDirection]>;
}
